import React from "react";

import { useGetApplicationQuery, useGetPlanCostsQuery, useGetPlansQuery } from "EmployeeApplication/api";
import { AccidentContextProvider } from "EmployeeCoverage/Components/Accident/accident.context";
import { AccidentEnrollmentModals } from "EmployeeCoverage/Components/Accident/accident.modals";
import { AccidentMultiplePlans } from "EmployeeCoverage/Components/Accident/accident.multiplePlans";
import { AccidentPlan, PlanCost } from "Utils/types";
import { BasicLoading } from "ZeroRuby";

import { AccidentSinglePlan } from "./accident.singlePlan";

type Data =
  | {
      isLoading: true;
      plans: any;
      planCosts: any;
    }
  | {
      isLoading: false;
      plans: Array<AccidentPlan>;
      planCosts: Array<PlanCost>;
    };

const useData = (): Data => {
  const { isLoading: isLoadingApplication, data: { employeePolicy: { id } = {} } = {} } = useGetApplicationQuery();
  const { isLoading: isLoadingPlans, data: { plans = [] } = {} } = useGetPlansQuery(
    {
      id,
      type: "accident",
    },
    { skip: !id },
  );
  const { isLoading: isLoadingCosts, data: { planCosts } = {} } = useGetPlanCostsQuery(
    {
      id,
      type: "accident",
    },
    { skip: plans.length === 0 },
  );

  return {
    isLoading: isLoadingApplication || isLoadingPlans || isLoadingCosts,
    plans,
    planCosts,
  } as Data;
};

/**
 * This component manages what to render as accident plans and costs are loaded.
 */
const StateManager = () => {
  const { isLoading, plans = [], planCosts = [] } = useData();
  if (isLoading) {
    return <BasicLoading />;
  }
  if (plans.length === 1) {
    return <AccidentSinglePlan plan={plans[0]} planCost={planCosts.find(({ planID }) => planID === plans[0].id)} />;
  }
  if (plans.length > 1) {
    return <AccidentMultiplePlans plans={plans} planCosts={planCosts} />;
  }
  return null;
};

export const AccidentPage = () => (
  <AccidentContextProvider>
    <StateManager />
    <AccidentEnrollmentModals />
  </AccidentContextProvider>
);
